<template>
  <div class="lyrics-view-modal-wrapper">
    <modal
      name="lyricsView"
      :adaptive="true"
      :scrollable="true"
      height="auto"
      width="800px"
      class="u-text-align-center"
      @closed="onClosed">
      <div class="lyrics-view-modal">
        <div
          v-if="selectedTrack"
          class="lyrics-view-modal__body">
          <dl class="lyrics-view-modal__meta">
            <div class="lyrics-view-modal__meta-row">
              <dt>商品タイトル名</dt>
              <dd>{{ productTitle }}</dd>
            </div>
            <div class="lyrics-view-modal__meta-row">
              <dt>楽曲名</dt>
              <dd>{{ selectedTrack.title_ja || selectedTrack.title_en }}</dd>
            </div>
            <div class="lyrics-view-modal__meta-row">
              <dt>アーティスト名</dt>
              <dd>{{ artistNames }}</dd>
            </div>
            <div class="lyrics-view-modal__meta-row">
              <dt>歌詞タイプ</dt>
              <dd>{{ lyricsTypeLabel }}</dd>
            </div>
          </dl>
          <pre
            v-if="selectedTrack.lyrics_type === 1"
            class="lyrics-view-modal__plain-text">{{ plainLyricsText }}</pre>
          <div
            v-else-if="selectedTrack.lyrics_type === 2"
            class="lyrics-view-modal__sync">
            <sync-audio-player
              ref="syncPlayer"
              :key="selectedTrack.id"
              :url="selectedTrack.audio_file_url"
              :current-play-url="currentPlayAudioUrl"
              :duration-string="durationMs"
              :lock-preview="true"
              :hide-reset="true"
              @timeupdate="onTimeupdate"
              @play-start="onPlayStart"
              @reset="onResetPlayback" />
            <div
              v-show="syncPreviewVisible"
              class="lyrics-view-modal__sync-lyric-wrap border-wrap">
              <div class="two-col dark-bg">
                <div class="time-bar">
                  <span>LINE</span>
                  <span>TIME</span>
                </div>
                <div class="right-section"></div>
              </div>
              <div class="lyrics-view-modal__sync-lyric line_sync_preview_inner group-form invert">
                <div class="preview_row sticky first-row"></div>
                <div class="lyric">
                  <lyric-scroll
                    :lyric="syncedLyric"
                    :lyric-active-class="'lyric-active'"
                    :lyric-highlighted-class="'lyric-word'"
                    :lyric-center-class="'lyric-center'"
                    triangle-width="14px"
                    triangle-color="#fff"
                    center-line-color="#707070"
                    center-time-color="#707070"
                    :current-time="currentTime" />
                </div>
              </div>
            </div>
            <h4 class="lyrics-view-modal__all-lyrics-heading">歌詞全文</h4>
            <pre class="lyrics-view-modal__plain-text">{{ plainLyricsText }}</pre>
          </div>
        </div>
        <div
          v-else
          class="lyrics-view-modal__empty">
          歌詞は登録されていません。
        </div>
        <div class="c-btn-wrap">
          <a
            href="#"
            class="c-btn c-btn--close c-btn--narrow js-modal-close"
            @click.prevent="hide">
            閉じる
            <span class="c-btn__icon-close">
              <span></span>
            </span>
          </a>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import SyncAudioPlayer from '../../SyncAudioPlayer'
import LyricScroll from '../../LyricsScroll'

export default {
  name: 'LyricsViewModal',
  components: {
    SyncAudioPlayer,
    LyricScroll,
  },
  props: {
    isShown: {
      type: Boolean,
      default: false,
    },
    discs: {
      type: Array,
      default: () => [],
    },
    productId: {
      type: [Number, String],
      default: null,
    },
    productTitle: {
      type: String,
      default: '',
    },
    initialDiscIndex: {
      type: Number,
      default: 0,
    },
    initialTrackIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      currentPlayAudioUrl: '',
      currentTime: 0,
      // 開いた直後にAPIから取得し直した最新のdiscs(登録直後などdiscsプロップが
      // 古い場合に対応するため)。取得できるまではdiscsプロップを使う
      fetchedDiscs: null,
      // 歌詞入力画面のプレビュー(SyncForm.vue)は、プレビューが非表示(v-show=false)の
      // 間に歌詞データが確定するため、LyricScrollの初期スクロール位置計算が高さ0で
      // 行われ、結果的に先頭行がそのまま表示されて上部の余白が出ない。
      // このモーダルは表示された状態で歌詞データが後から確定するため、同じ挙動には
      // ならず先頭行を中央寄せしようとして大きな余白ができてしまう。歌詞データが
      // 確定するまでプレビューを隠しておくことで、同じ挙動になるようにしている。
      syncPreviewVisible: false,
    }
  },
  computed: {
    effectiveDiscs() {
      return this.fetchedDiscs || this.discs
    },
    selectedTrack() {
      const disc = this.effectiveDiscs[this.initialDiscIndex]
      const track = disc && disc.tracks[this.initialTrackIndex]
      return track && track.lyrics_type > 0 ? track : null
    },
    artistNames() {
      const artists = (this.selectedTrack && this.selectedTrack.main_artists) || []
      return artists.map(artist => artist.name_ja || artist.name_en).join('、')
    },
    lyricsTypeLabel() {
      if (!this.selectedTrack) {
        return ''
      }
      return this.selectedTrack.lyrics_type === 2 ? '行同期歌詞' : '非同期歌詞'
    },
    // type=1/2 とも登録時は同一のJSON( {lines:[{words:[{str}]}]} )形式で保存される。
    // 旧形式(生テキスト)のデータも残っているため、SimpleForm.vueのfetchLyrics()と
    // 同じ判定方法でJSON/生テキストの両方に対応する
    plainLyricsText() {
      const { selectedTrack } = this
      const lyrics = selectedTrack && selectedTrack.lyrics && selectedTrack.lyrics.lyrics
      if (!lyrics) {
        return ''
      }
      if (!lyrics.startsWith('{"lines"')) {
        return lyrics
      }
      try {
        const parsed = JSON.parse(lyrics)
        return (parsed.lines || [])
          .map(line => (line.words && line.words[0] && line.words[0].str) || '')
          .join('\n')
      } catch (e) {
        return ''
      }
    },
    // SyncAudioPlayer.vueはdurationStringをミリ秒の数値として扱う(SyncForm.vueの
    // updateAudio()と同じ変換: "mm:ss"形式の文字列を秒に変換して1000倍する)
    durationMs() {
      const duration = this.selectedTrack && this.selectedTrack.duration
      if (!duration) {
        return 0
      }
      const seconds = duration.split(':').reduce((acc, time) => (60 * acc) + Number(time), 0)
      return seconds * 1000
    },
    syncedLyric() {
      const lyrics = this.selectedTrack && this.selectedTrack.lyrics_type === 2
        && this.selectedTrack.lyrics && this.selectedTrack.lyrics.lyrics
      if (!lyrics) {
        return []
      }
      try {
        const lyricObj = JSON.parse(lyrics)
        return (lyricObj.lines || []).map(line => [
          line.start !== null ? line.start / 1000 : null,
          (line.words && line.words[0] && line.words[0].str) || '',
        ])
      } catch (e) {
        return []
      }
    },
  },
  watch: {
    isShown() {
      if (this.isShown) {
        this.show()
      } else {
        this.hide()
      }
    },
  },
  methods: {
    show() {
      // discsプロップ(セッションストレージ由来)は歌詞登録直後だと本文が
      // 反映されていないことがあるため、開くたびに一旦クリアしAPIから取り直す
      this.fetchedDiscs = null
      this.currentTime = 0
      this.currentPlayAudioUrl = ''
      this.syncPreviewVisible = false
      this.$modal.show('lyricsView')
      this.fetchLatestDiscs()
    },
    async fetchLatestDiscs() {
      if (this.productId) {
        try {
          const { data } = await window.axios.get(`/api/client/products/${this.productId}/release`)
          this.fetchedDiscs = data.discs || []
        } catch (e) {
          // 取得に失敗した場合はdiscsプロップの内容をそのまま表示する
        }
      }
      // 歌詞データが確定してからLyricScrollの初期位置計算が行われるよう、
      // 再描画が完了するまで待ってからプレビューを表示する
      await this.$nextTick()
      await this.$nextTick()
      await this.$nextTick()
      this.syncPreviewVisible = true
    },
    hide() {
      this.$modal.hide('lyricsView')
    },
    onClosed() {
      this.$emit('closed')
    },
    onTimeupdate(time) {
      this.currentTime = time
    },
    onPlayStart(url) {
      this.currentPlayAudioUrl = url
    },
    // 閲覧画面なので歌詞タイミングをリセットする概念はなく、再生位置を先頭に戻す
    onResetPlayback() {
      if (this.$refs.syncPlayer) {
        this.$refs.syncPlayer.onStartFromBegin()
      }
    },
  },
}
</script>

<style scoped>
.lyrics-view-modal {
  padding: 2vmin;
  text-align: left;
}
.lyrics-view-modal__meta {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 16px;
}
.lyrics-view-modal__meta-row {
  display: flex;
  font-size: 13px;
  line-height: 1.8;
}
.lyrics-view-modal__meta-row + .lyrics-view-modal__meta-row {
  margin-top: 4px;
}
.lyrics-view-modal__meta-row dt {
  width: 110px;
  flex-shrink: 0;
  color: #707070;
}
.lyrics-view-modal__meta-row dd {
  margin: 0;
  font-weight: bold;
}
.lyrics-view-modal__plain-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  max-height: 50vh;
  overflow-y: auto;
}
/* プレビューON時、SyncAudioPlayer.vueの.player-barはopacity:0.9の
   スタイルにより独自のスタッキングコンテキストを持つ。歌詞入力画面
   (SyncForm.vue)ではプレビュー領域がプレーヤーより上に配置されるため
   表面化しないが、このモーダルではプレーヤーの下にプレビュー領域を
   置いているため、後続の兄弟要素であるプレビュー領域(歌詞リスト)が
   z-index上プレーヤーより手前に描画され、再生速度プルダウンの一部が
   隠れてしまう。プレーヤー側の重なり順を明示的に引き上げて解消する。 */
.lyrics-view-modal ::v-deep .player-bar {
  position: relative;
  z-index: 2;
}
/* 同期歌詞プレビューは、C-MAP(narasu本体の同期歌詞フォーム/管理画面と共通)と
   全く同じマークアップ(.border-wrap .group-form 等)・グローバルCSS(_syncForm.scss)を
   使うことで、LINE/TIMEヘッダーや文字色を含めて見た目を完全に一致させている。
   このコンポーネント独自のスタイルは最小限の余白調整のみ。 */
.lyrics-view-modal__sync-lyric-wrap {
  margin-top: 12px;
  border-radius: 4px;
  overflow: hidden;
}
/* LINE/TIME/歌詞テキストの列幅を、ヘッダー行とデータ行で完全に一致させる。
   列幅は %指定ではなく、LINE("999"等の行番号)・TIME("00:00.000"という
   ミリ秒まで含む表示)がどの画面幅でも欠けずに収まる固定px幅にしている
   (フォントサイズは歌詞テキストと同じにする方針のため、幅の方を内容に
   合わせて広げている)。歌詞列は残り幅をすべて1frで受け取る。
   モーダルはvue-js-modalのadaptiveにより画面幅に応じて連続的に縮むため、
   特定のブレークポイントだけで切り替える実装にすると、その境界の前後で
   列崩れが発生する。そのため画面幅を問わず常にこの固定px幅を適用する。

   ヘッダー行は .two-col.dark-bg > .time-bar(LINE/TIME) + .right-section(歌詞列)
   という2階層構造のため、当初は .time-bar を display:contents にして
   LINE/TIMEのspanを親グリッドに直接参加させ、データ行(.left-col、3列を
   直接持つフラットな構造)と列位置を揃えていた。しかしSafari(WebKit)は
   display:contents とCSS Gridの組み合わせに長年既知の不具合があり
   (contents化した要素の子がgridアイテムとして正しく扱われず、SP(iOS Safari)
   で表示が崩れる)、そのため display:contents は使わず、.time-bar 自身を
   LINE+TIME分の2列グリッド(60px 80px)として扱い、外側の .two-col.dark-bg
   はそれをまとめた1列(140px)+歌詞列(1fr)の2列グリッドにすることで、
   display:contents 無しで同じ列位置を実現している。 */
.lyrics-view-modal__sync-lyric-wrap ::v-deep .two-col.dark-bg {
  grid-template-columns: 140px 1fr;
  height: 36px;
  padding: 4px 0;
}
.lyrics-view-modal__sync-lyric-wrap ::v-deep .time-bar {
  display: grid;
  grid-template-columns: 60px 80px;
  gap: 0;
}
.lyrics-view-modal__sync-lyric-wrap ::v-deep .time-bar span {
  align-self: center;
  text-align: center;
  padding-left: 0;
}
.lyrics-view-modal__sync-lyric ::v-deep .left-col {
  grid-template-columns: 60px 80px 1fr;
  gap: 0;
  /* デフォルト(stretch)だと各列が行の高さいっぱいに伸び、フォントサイズが
     異なる列があると、テキストの垂直位置が微妙にずれるため、各列を内容の
     高さぶんだけにして中央揃えにする */
  align-items: center;
}
.lyrics-view-modal__sync-lyric ::v-deep .line-num {
  padding-left: 0;
  text-align: center;
}
.lyrics-view-modal__sync-lyric ::v-deep .start-time {
  text-align: center;
}
.lyrics-view-modal__sync-lyric ::v-deep .center-col {
  padding: 0 8px;
}
/* SP(狭い画面幅)では、音声プレーヤーのコントロール一式が横一列に収まりきらず
   崩れるため、再生アイコン群を1段目、秒数表示を2段目、シークバーを3段目、
   再生速度を4段目に、それぞれ明示的に折り返して表示する。
   RESETボタンは頭出しボタンと機能が重複するため非表示にしている(hide-reset)。 */
@media screen and (max-width: 480px) {
  .lyrics-view-modal ::v-deep .player-bar {
    flex-wrap: wrap;
    row-gap: 8px;
    column-gap: 4px;
    padding: 10px 12px;
    /* 通常時はjustify-content:flex-endで右寄せだが、SPではシークバー等が
       それぞれ独立した行になり残りの再生アイコン群だけの行に空きができるため、
       他の行(中央寄せ)と揃うようこの行だけ中央寄せに上書きする */
    justify-content: center;
  }
  .lyrics-view-modal ::v-deep .player-bar .time {
    order: 1;
    flex-basis: 100%;
    text-align: center;
    font-size: 10px;
    margin-right: 0;
    white-space: nowrap;
  }
  .lyrics-view-modal ::v-deep .player-bar .seekbar {
    order: 2;
    flex-basis: 100%;
    margin: 4px 0;
  }
  .lyrics-view-modal ::v-deep .player-bar .preview {
    order: 3;
    flex-basis: 100%;
    flex-direction: row;
    justify-content: center;
  }
  .lyrics-view-modal ::v-deep .player-bar .control img {
    width: 32px;
    height: 32px;
    margin: 0;
  }
  .lyrics-view-modal ::v-deep .player-bar .start-btn img,
  .lyrics-view-modal ::v-deep .player-bar .five-rewind img,
  .lyrics-view-modal ::v-deep .player-bar .five-back img {
    width: 22px;
    height: 22px;
  }
}
.lyrics-view-modal__all-lyrics-heading {
  margin: 16px 0 4px;
  font-size: 13px;
  color: #707070;
}
.lyrics-view-modal__empty {
  padding: 24px 0;
  text-align: center;
  color: #707070;
}
</style>
